import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { usage } from "@/api/admin";
import { Badge, Card, CardBody, CardHeader, Input, Label } from "@/components/ui";

/**
 * Read-only usage dashboard. Shows period buckets (group by event_type)
 * and a tail of raw events. Powers the future Stripe metered-billing
 * exporter that will read directly from `usage_events`.
 */
export function AdminUsagePage() {
  const [from, setFrom] = useState<string>(daysAgo(30));
  const [to, setTo] = useState<string>(today());
  const [eventType, setEventType] = useState<string>("");

  const summary = useQuery({
    queryKey: ["usage-summary", from, to, eventType],
    queryFn: () =>
      usage.summary({
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        event_type: eventType || undefined,
      }),
  });
  const events = useQuery({
    queryKey: ["usage-events", from, to, eventType],
    queryFn: () =>
      usage.events({
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        event_type: eventType || undefined,
        limit: 100,
      }),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Usage & billing</h1>

      <Card>
        <CardBody>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div>
              <Label>Event type (optional)</Label>
              <Input
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                placeholder="orders.placed"
              />
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Summary by event type</h2>
        </CardHeader>
        <CardBody>
          {summary.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : !summary.data || summary.data.buckets.length === 0 ? (
            <p className="text-sm text-slate-500">No usage in the selected window.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Event type</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {summary.data.buckets.map((b) => (
                  <tr key={b.event_type} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-2 font-mono text-xs">{b.event_type}</td>
                    <td className="font-medium">{Number(b.quantity).toLocaleString()}</td>
                    <td className="text-xs text-slate-500">{b.unit}</td>
                    <td className="text-xs text-slate-500">{b.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">
            Recent events <Badge>{events.data?.length ?? 0}</Badge>
          </h2>
        </CardHeader>
        <CardBody className="overflow-x-auto">
          {events.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">When</th>
                  <th>Event</th>
                  <th>Qty</th>
                  <th>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {events.data?.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-2 text-xs text-slate-500">
                      {new Date(e.occurred_at).toLocaleString()}
                    </td>
                    <td className="font-mono text-xs">{e.event_type}</td>
                    <td>{e.quantity}</td>
                    <td className="text-xs text-slate-500">
                      <code>{JSON.stringify(e.metadata)}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(d: number) {
  const t = new Date();
  t.setDate(t.getDate() - d);
  return t.toISOString().slice(0, 10);
}
